import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { AR_SECTOR, arMap } from "@/i18n_halal";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { OwlLoader } from "@/components/OwlLoader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    eyebrow: "Live screener",
    title: "Live from the screener, right now",
    sub: "Real prices, real verdicts. These are the largest Shariah compliant names we carry — tap one to read the numbers behind the verdict, no account needed.",
    compliant: "Shariah compliant",
    purify: "impure income",
    all: (n) => `See all ${n.toLocaleString()} Shariah compliant names`,
    loading: "Pulling live prices…",
  },
  ar: {
    eyebrow: "الفارز المباشر",
    title: "مباشر من الفارز، الآن",
    sub: "أسعار حقيقية وأحكام حقيقية. هذه أكبر الأسهم المتوافقة مع الشريعة التي ندرجها — اضغط على أي منها لقراءة الأرقام التي بُني عليها الحكم، بلا حساب.",
    compliant: "متوافق مع الشريعة",
    purify: "إيراد غير جائز",
    all: (n) => `اطلع على ${n.toLocaleString("ar-QA")} سهماً متوافقاً مع الشريعة`,
    loading: "جارٍ جلب الأسعار المباشرة…",
  },
};

/** A live slice of the public screener, so visitors see real data before they sign up. */
export const ScreenerTeaser = ({ market = "All", limit = 6 }) => {
  const navigate = useNavigate();
  const { lang } = useApp();
  const locale = lang === "ar" ? "ar" : "en";
  const c = COPY[locale];
  const [d, setD] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/halal-screener`, {
      params: { verdict: "Compliant", sort: "market_cap", market, limit },
    }).then((r) => setD(r.data)).catch(() => setD({ items: [], total: 0 }));
  }, [market, limit]);

  return (
    <section data-testid="screener-teaser">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[#3E2E65]">
        <ShieldCheck className="h-3.5 w-3.5" />{c.eyebrow}
      </div>
      <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">{c.title}</h2>
      <p className="mt-3 max-w-2xl text-muted-foreground">{c.sub}</p>

      {!d ? (
        <p className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
          <OwlLoader size="sm" label={c.loading} />
        </p>
      ) : (
        <>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {d.items.map((r) => (
              <button key={r.symbol} onClick={() => navigate(`/halal-stocks/${encodeURIComponent(r.symbol)}`)}
                className="text-start rounded-2xl border border-border bg-card p-5 transition-shadow hover:shadow-premium group"
                data-testid={`teaser-${r.symbol}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-bold">{r.symbol}</div>
                    <div className="text-[11px] text-muted-foreground truncate max-w-[150px]">{r.name}</div>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                    <ShieldCheck className="h-3 w-3" />{c.compliant}
                  </span>
                </div>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <span className="numeric text-lg font-semibold">
                      {r.price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-[10px] text-muted-foreground ms-1">{r.currency}</span>
                  </div>
                  <span className={`numeric text-xs font-semibold ${
                    r.change_pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {r.change_pct >= 0 ? "+" : ""}{r.change_pct}%
                  </span>
                </div>
                <div className="mt-3 flex items-center justify-between text-[10px] text-muted-foreground">
                  <span>{r.market} · {locale === "ar" ? arMap(AR_SECTOR, r.sector) : r.sector}</span>
                  <span className="numeric">{r.interest_income_pct ?? 0}% {c.purify}</span>
                </div>
              </button>
            ))}
          </div>
          <button onClick={() => navigate("/top-halal-stocks")}
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#3E2E65] hover:gap-2.5 transition-all"
            data-testid="teaser-see-all">
            {c.all(d.total || 0)} <ArrowRight className="h-4 w-4" />
          </button>
        </>
      )}
    </section>
  );
};
